import { useEffect, useMemo } from "react";
import { Effect } from "postprocessing";
import { Uniform } from "three";

// FPV-lens barrel distortion. mainUv remaps the sample position for the whole
// merged effect pass (so Bloom's overlay distorts consistently with the scene):
//   r_in = r_out * (1 + k*r_out²) / (1 + k*R²)
// with R the corner radius — corners map to corners, so nothing samples outside
// the frame; the centre magnifies and the edges compress, the classic GoPro
// look. Aspect-corrected so the bulge is round on any window.
const frag = /* glsl */ `
  uniform float uK;

  void mainUv(inout vec2 uv) {
    if (uK <= 0.0) { return; }
    float aspect = resolution.x / resolution.y;
    vec2 c = uv - 0.5;
    c.x *= aspect;
    float R2 = 0.25 * (aspect * aspect + 1.0);
    float r2 = dot(c, c);
    c *= (1.0 + uK * r2) / (1.0 + uK * R2);
    c.x /= aspect;
    uv = c + 0.5;
  }
`;

class FisheyeEffectImpl extends Effect {
  constructor() {
    super("FisheyeEffect", frag, { uniforms: new Map([["uK", new Uniform(0)]]) });
  }
}

const MAX_K = 1.2; // distortion at slider 1.0 — tuned alongside the 100→140° FOV ramp

/** The fisheye lens as a composer child; strength 0..1 from the FPV panel. */
export function FisheyeFx({ strength }: { strength: number }) {
  const effect = useMemo(() => new FisheyeEffectImpl(), []);
  useEffect(() => {
    (effect.uniforms.get("uK") as Uniform).value = Math.max(0, strength) * MAX_K;
  }, [effect, strength]);
  return <primitive object={effect} dispose={null} />;
}
